import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getAppConfig } from '../lib/appsRegistry';
import Authenticity from '../lib/Authenticity';
import {
  AUTH_TOKEN,
  AUTH_TOKEN_HEADER,
  REFRESH_TOKEN_HEADER,
} from '../../shared/constants/network';

const getServerURL = () => {
  const { origin } = window.location;
  return origin;
};

const mainLink = new HttpLink({ uri: `${getServerURL()}/graphql` });

const authMiddleware = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem(AUTH_TOKEN);
  let authHeaders = {
    [AUTH_TOKEN_HEADER]: `Bearer ${token}` || '',
  };
  authHeaders = Authenticity.authenticityHeaders(authHeaders);
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...authHeaders,
    },
  }));

  return forward(operation);
});

const afterwareLink = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;

    if (headers) {
      const refreshToken = headers.get(REFRESH_TOKEN_HEADER);
      if (refreshToken) {
        localStorage.setItem(AUTH_TOKEN, refreshToken);
      }
    }

    return response;
  })
);

const trackingMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'recent-activity': localStorage.getItem('lastOnlineTime') || null,
    },
  }));

  return forward(operation);
});

const rootNamespaces = [
  'BasketAnalysisQueries',
  'BasketAnalysisMutations',
  'RetailExperimentsQueries',
  'RetailExperimentsMutations',
  'BadashQueries',
  'BadashMutations',
  'AttributesQueries',
  'FeaturesQueries',
  'GlobalSearchQueries',
  'MopQueries',
  'MopMutations',
  'FeasibilityQueries',
  'FeasibilityMutations',
  'OperatorQueries',
  'OperatorMutations',
  'SatQueries',
  'SatMutations',
  'TenderOptQueries',
  'TenderOptMutations',
];

const badashTypePolicies = {
  Dashboard: {
    keyFields: ['id', 'slug'],
  },
  DatasourceResult: {
    keyFields: ['queryDef', 'parameters'],
  },
};

const operatorTypePolicies = {
  // We prevent `App` objects from combining so we can preserve the unique `scopes` field
  // to stay relevant to the `User` object that the `App` object may be returned with
  App: {
    keyFields: false,
  },
};

const globalSearchTypePolicies = {
  SearchResultItem: {
    keyFields: ['id', 'type'],
  },
};

const cache = new InMemoryCache({
  typePolicies: {
    ...rootNamespaces.reduce((acc, val) => {
      acc[val] = { merge: true };
      return acc;
    }, {}),
    ...badashTypePolicies,
    ...operatorTypePolicies,
    ...globalSearchTypePolicies,
  },
});

export default async (props = {}) => {
  const { company } = props;

  let companyName = null;
  if (company) {
    companyName = company.subdomain;
  }

  /*
  This is for specifiyign additional external URI's in any app.
  The URI can be referenced by passing clientName as part of the Apollo context.
  The default one should always be the `mainLink` which is the ruby server/
   */
  const appConfig = await getAppConfig({ companyName });

  let httpLink = mainLink;
  if (appConfig.externalAPIs) {
    for (const api of appConfig.externalAPIs) {
      httpLink = ApolloLink.split(
        operation => operation.getContext().clientName === api.clientName,
        api.httpLink,
        httpLink
      );
    }
  }

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      console.log(graphQLErrors);
    }

    if (networkError) {
      console.log(networkError);
    }

    if (props.onError) {
      props.onError({ graphQLErrors, networkError });
    }
  });

  const link = ApolloLink.from([
    authMiddleware,
    trackingMiddleware,
    errorLink,
    afterwareLink,
    httpLink,
  ]);

  return new ApolloClient({
    link,
    cache,
  });
};
